import React, { useState } from 'react';
import { Field } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import css from './avatar-upload.module.css';
import { denormalisedResponseEntities } from '../../../util/data';
import Avatar from '../../../components/Avatar/Avatar';

const ACCEPT_IMAGES = 'image/*';

const onImageUploadHandler = values => {
  const { id, file } = values;
  if (file) {
    const bodyParams = {
      image: file,
    };
    const queryParams = {
      expand: true,
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    };

    return window?.app?.sdk.images.upload(bodyParams, queryParams).then(resp => {
      const uploadedImage = resp.data.data;
      return { data: { id: uploadedImage?.id?.uuid } };
    });
  }
};

const onUpdateProfile = values => {
  const queryParams = {
    expand: true,
    include: ['profileImage'],
    'fields.image': ['variants.square-small', 'variants.square-small2x'],
  };

  return window?.app?.sdk.currentUser.updateProfile(values, queryParams).then(response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the sdk.currentUser.updateProfile response');
    }
    const currentUser = entities[0];

    // Update current user in state.user.currentUser through user.duck.js
    return currentUser;
  });
};

const AvatarUploadField = ({ user, form }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const profileImage = { imageId: user?.profileImage?.id?.uuid };
  const fileExists = !!profileImage?.file;
  const fileUploadInProgress = false;
  const delayAfterUpload = 10;
  const uploadImageError = null;
  const uploadInProgress = false;

  const transientUser = { ...user, profileImage: currentUser?.profileImage || user?.profileImage };

  const isUploadImageOverLimitError = error => {
    return error && error.includes('Upload image over limit');
  };

  const handleSubmitProfileImage = async imageId => {
    const updatedValues = { profileImageId: imageId };

    const results = await onUpdateProfile(updatedValues);
    setCurrentUser(results);
  };

  const imageFromFile =
    fileExists && (fileUploadInProgress || delayAfterUpload) ? (
      <ImageFromFile
        id={profileImage.id}
        className={errorClasses}
        rootClassName={css.uploadingImage}
        aspectWidth={1}
        aspectHeight={1}
        file={profileImage.file}
      >
        {uploadingOverlay}
      </ImageFromFile>
    ) : null;

  const avatarComponent =
    !fileUploadInProgress && profileImage.imageId ? (
      <Avatar
        className={css.avatar}
        renderSizes="(max-width: 767px) 96px, 240px"
        user={transientUser}
        disableProfileLink
      />
    ) : null;

  const chooseAvatarLabel =
    profileImage.imageId || fileUploadInProgress ? (
      <div className={css.avatarContainer}>
        {imageFromFile}
        {avatarComponent}
        <div className={css.changeAvatar}>
          <FormattedMessage id="ProfileSettingsForm.changeAvatar" />
        </div>
      </div>
    ) : (
      <div className={css.avatarPlaceholder}>
        <div className={css.avatarPlaceholderText}>
          <FormattedMessage id="ProfileSettingsForm.addYourProfilePicture" />
        </div>
        <div className={css.avatarPlaceholderTextMobile}>
          <FormattedMessage id="ProfileSettingsForm.addYourProfilePictureMobile" />
        </div>
      </div>
    );

  return (
    <Field
      accept={ACCEPT_IMAGES}
      id="profileImage"
      name="profileImage"
      label={chooseAvatarLabel}
      type="file"
      form={null}
      uploadImageError={uploadImageError}
      disabled={uploadInProgress}
    >
      {fieldProps => {
        const { accept, id, input, label, disabled, uploadImageError } = fieldProps;
        const { name, type } = input;
        const onChange = async e => {
          const file = e.target.files[0];
          form?.change(`profileImage`, file);
          form?.blur(`profileImage`);
          if (file != null) {
            const tempId = `${file.name}_${Date.now()}`;
            const result = await onImageUploadHandler({ id: tempId, file });
            handleSubmitProfileImage(result?.data?.id);
          }
        };

        let error = null;

        if (isUploadImageOverLimitError(uploadImageError)) {
          error = (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.imageUploadFailedFileTooLarge" />
            </div>
          );
        } else if (uploadImageError) {
          error = (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.imageUploadFailed" />
            </div>
          );
        }

        return (
          <div className={css.uploadAvatarWrapper}>
            <label className={css.label} htmlFor={id}>
              {label}
            </label>
            <input
              accept={accept}
              id={id}
              name={name}
              className={css.uploadAvatarInput}
              disabled={disabled}
              onChange={onChange}
              type={type}
            />
            {error}
          </div>
        );
      }}
    </Field>
  );
};

export default AvatarUploadField;
